import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Icon,
  Badge,
  Button,
  Divider,
  List,
  ListItem,
  ListIcon,
} from "@chakra-ui/react";
import { FaCheckCircle, FaShieldAlt, FaChartLine, FaUniversity, FaHandHoldingUsd, FaFileInvoiceDollar } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const SAND = "#C2A878";

const FinanceServicesSMSF: React.FC = () => {
  const benefits = [
    {
      icon: FaShieldAlt,
      title: "Control & Flexibility",
      description: "Direct control over your super investments with competitive SMSF loan rates",
    },
    {
      icon: FaChartLine,
      title: "Tax Advantages",
      description: "Maximise tax benefits while building wealth through property in your SMSF",
    },
    {
      icon: FaUniversity,
      title: "Asset Protection",
      description: "Protect your retirement assets with compliant SMSF property structures",
    },
    {
      icon: FaHandHoldingUsd,
      title: "Competitive Rates",
      description: "Access exclusive SMSF loan rates typically not available to retail customers",
    },
    {
      icon: FaFileInvoiceDollar,
      title: "Expert Compliance",
      description: "Navigate complex SMSF regulations with our specialist finance team",
    },
    {
      icon: FaChartLine,
      title: "Wealth Building",
      description: "Leverage your super to acquire investment property for retirement growth",
    },
  ];

  const eligibilityRequirements = [
    {
      title: "SMSF Structure Requirements",
      items: [
        "SMSF must be compliant and have an Australian Business Number (ABN)",
        "Trust deed must allow for property investment and borrowing",
        "Bare trust or custodian arrangement must be established",
        "All trustees must be signatories to the loan",
        "SMSF must have sufficient cash for deposit (typically 30-40%)",
      ],
    },
    {
      title: "Property Requirements",
      items: [
        "Property must meet the sole purpose test for retirement benefits",
        "Cannot be purchased from or leased to SMSF members or relatives",
        "Must be a single acquirable asset under LRBA rules",
        "Residential or commercial property acceptable",
        "Property cannot be altered or improved using SMSF funds during loan term",
      ],
    },
    {
      title: "Financial Requirements",
      items: [
        "Minimum loan amount typically $200,000",
        "LVR (Loan to Value Ratio) usually capped at 70%",
        "SMSF must demonstrate serviceability from fund income",
        "May require personal guarantees from trustees",
        "Sufficient SMSF balance to cover fees, stamp duty, and ongoing costs",
      ],
    },
    {
      title: "Documentation Requirements",
      items: [
        "SMSF trust deed and establishment documents",
        "Latest financial statements and tax returns",
        "Investment strategy document",
        "Member statements showing contributions and balances",
        "Bare trust or custodian deed",
        "Property valuation and contract of sale",
      ],
    },
  ];

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/client/finance/services/smsf"
        fallbackTitle="SMSF Loans - Self Managed Super Fund Property Finance"
        fallbackDescription="Unlock your super's potential with SMSF property loans. Competitive rates, expert compliance support, and flexible lending solutions for your self-managed super fund."
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} align="center" textAlign="center">
            <Badge bg={SAND} color={NAVY} fontSize="md" px={4} py={2} borderRadius="full">
              SMSF Specialist Finance
            </Badge>
            <Heading as="h1" size="2xl" fontWeight="bold">
              SMSF Property Loans
            </Heading>
            <Text fontSize="xl" maxW="3xl" opacity={0.95}>
              Leverage your self-managed super fund to acquire investment property with competitive rates
              and expert compliance guidance from Australia's SMSF lending specialists.
            </Text>
            <HStack spacing={4} pt={4}>
              <Button
                size="lg"
                bg={SAND}
                color={NAVY}
                _hover={{ opacity: 0.9 }}
                rightIcon={<Icon as={FaCheckCircle} />}
              >
                Check Eligibility
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: "whiteAlpha.200" }}
              >
                Speak to SMSF Expert
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Benefits Grid */}
      <Box py={20} bg="gray.50">
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="xl" color={NAVY}>
                Why Choose SMSF Property Lending?
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="2xl">
                Self-managed super funds offer unique opportunities to build wealth through property investment
                while maintaining control and maximising tax benefits.
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8} w="full">
              {benefits.map((benefit, index) => (
                <Box
                  key={index}
                  bg="white"
                  p={8}
                  borderRadius="xl"
                  shadow="md"
                  border="1px solid"
                  borderColor="gray.200"
                  transition="all 0.3s"
                  _hover={{
                    transform: "translateY(-4px)",
                    shadow: "xl",
                    borderColor: SAND,
                  }}
                >
                  <VStack align="start" spacing={4}>
                    <Icon as={benefit.icon} boxSize={12} color={SAND} />
                    <Heading as="h3" size="md" color={NAVY}>
                      {benefit.title}
                    </Heading>
                    <Text color="gray.600">{benefit.description}</Text>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Key Features */}
      <Box py={20}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
            <VStack align="start" spacing={6}>
              <Badge bg={SAND} color={NAVY} fontSize="sm" px={3} py={1} borderRadius="full">
                SMSF Loan Features
              </Badge>
              <Heading as="h2" size="xl" color="gray.800">
                Specialized SMSF Lending Solutions
              </Heading>
              <Text fontSize="lg" color="gray.600">
                Our SMSF loans are specifically designed for self-managed super funds, ensuring compliance
                with all ATO regulations and superannuation laws.
              </Text>
              <List spacing={4}>
                <ListItem display="flex" alignItems="start">
                  <ListIcon as={FaCheckCircle} color={SAND} mt={1} boxSize={5} />
                  <Text><strong>Limited Recourse Borrowing:</strong> Compliant LRBA structures protecting SMSF assets</Text>
                </ListItem>
                <ListItem display="flex" alignItems="start">
                  <ListIcon as={FaCheckCircle} color={SAND} mt={1} boxSize={5} />
                  <Text><strong>Competitive LVRs:</strong> Up to 70% LVR for residential, 60% for commercial</Text>
                </ListItem>
                <ListItem display="flex" alignItems="start">
                  <ListIcon as={FaCheckCircle} color={SAND} mt={1} boxSize={5} />
                  <Text><strong>Interest Only Options:</strong> Maximise cash flow in your SMSF</Text>
                </ListItem>
                <ListItem display="flex" alignItems="start">
                  <ListIcon as={FaCheckCircle} color={SAND} mt={1} boxSize={5} />
                  <Text><strong>No Monthly Fees:</strong> Transparent pricing with no hidden charges</Text>
                </ListItem>
                <ListItem display="flex" alignItems="start">
                  <ListIcon as={FaCheckCircle} color={SAND} mt={1} boxSize={5} />
                  <Text><strong>Fast Approvals:</strong> Streamlined process with SMSF specialists</Text>
                </ListItem>
              </List>
            </VStack>

            <Box bg={NAVY} p={10} borderRadius="2xl" color="white">
              <VStack spacing={6} align="start">
                <Heading as="h3" size="lg">
                  Typical SMSF Loan Structure
                </Heading>
                <Divider borderColor="whiteAlpha.400" />
                <VStack align="stretch" spacing={4} w="full">
                  <HStack justify="space-between">
                    <Text fontWeight="semibold">Loan Amount:</Text>
                    <Text>$200,000 - $5,000,000+</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontWeight="semibold">LVR Range:</Text>
                    <Text>60% - 70%</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontWeight="semibold">Interest Rates:</Text>
                    <Text>From 6.24% p.a.*</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontWeight="semibold">Loan Term:</Text>
                    <Text>Up to 30 years</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontWeight="semibold">Repayment Type:</Text>
                    <Text>P&I or Interest Only</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontWeight="semibold">Setup Time:</Text>
                    <Text>4-6 weeks typical</Text>
                  </HStack>
                </VStack>
                <Divider borderColor="whiteAlpha.400" />
                <Text fontSize="sm" opacity={0.8}>
                  *Rates subject to SMSF assessment and market conditions. Comparison rate available on application.
                </Text>
                <Button
                  size="lg"
                  bg={SAND}
                  color={NAVY}
                  w="full"
                  _hover={{ opacity: 0.9 }}
                >
                  Get SMSF Quote
                </Button>
              </VStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Eligibility Requirements Accordion */}
      <Box py={20} bg="gray.50">
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="xl" color={NAVY}>
                SMSF Loan Eligibility Requirements
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="3xl">
                Understanding the requirements for SMSF property lending is crucial. Our specialist team
                will guide you through each step to ensure compliance and smooth processing.
              </Text>
            </VStack>

            <Accordion allowMultiple w="full">
              {eligibilityRequirements.map((section, index) => (
                <AccordionItem
                  key={index}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="lg"
                  mb={4}
                  bg="white"
                >
                  <AccordionButton
                    py={6}
                    px={8}
                    _hover={{ bg: "gray.50" }}
                    borderRadius="lg"
                  >
                    <Box flex="1" textAlign="left">
                      <Heading as="h3" size="md" color={NAVY}>
                        {section.title}
                      </Heading>
                    </Box>
                    <AccordionIcon color={SAND} boxSize={6} />
                  </AccordionButton>
                  <AccordionPanel px={8} py={6}>
                    <List spacing={3}>
                      {section.items.map((item, itemIndex) => (
                        <ListItem key={itemIndex} display="flex" alignItems="start">
                          <ListIcon as={FaCheckCircle} color={SAND} mt={1} />
                          <Text color="gray.700">{item}</Text>
                        </ListItem>
                      ))}
                    </List>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </VStack>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box py={20} bg={NAVY} color="white">
        <Container maxW="container.xl">
          <VStack spacing={8} textAlign="center">
            <Heading as="h2" size="xl">
              Ready to Leverage Your SMSF for Property Investment?
            </Heading>
            <Text fontSize="lg" maxW="2xl" opacity={0.95}>
              Speak with our SMSF lending specialists today for a complimentary consultation and discover
              how you can build wealth through property in your self-managed super fund.
            </Text>
            <HStack spacing={4}>
              <Button
                size="lg"
                bg={SAND}
                color={NAVY}
                _hover={{ opacity: 0.9 }}
              >
                Book SMSF Consultation
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: "whiteAlpha.200" }}
              >
                Download SMSF Guide
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceServicesSMSF;
